import alt from '../alt';
import {createStore} from '../alt';
import cookie from 'react-cookie';

import SingleObjectResource, {InvalidResponseCode} from '../resources/GenericResource';
import CurrentUserActions from '../actions/CurrentUserActions';


const CurrentUserSource = {
    getCurrentUser() {
        return {
            remote(state, lang) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiUserDetails).fetch().then((data) => {
                        var {email, first_name, last_name, can_apply} = data;

                        var profile = {};
                        Object.keys(data).forEach((key) => {
                            profile[key] = data[key];
                        });
                        resolve({email, first_name, last_name, can_apply, profile, language: lang});

                    }, (error) => {
                        if (error.statusCode !== 403) {
                            // Trigger a global error
                            CurrentUserActions.pageFault(error);
                            reject({language: lang});
                        } else {
                            reject({language: lang});
                        }
                    }).catch((error) => {
                        // Trigger a global error
                        CurrentUserActions.pageFault(error);
                        reject({language: lang});
                    });
                });
            },

            local(state) {
                // So we don't do the call again when rendering clientside
                // Note: We may want to recheck from backend after certain time interval
                state.userDataReceived = true;
                return state.cacheTimestamp ? state : null;
            },

            success: CurrentUserActions.receivedCurrentUser,
            error: CurrentUserActions.failedCurrentUser
        };
    },

    logout() {
        return {
            remote() {
                cookie.remove('filters');
                cookie.remove('remind_later_ids');
                return new SingleObjectResource(DJ_CONST.API.apiUserLogout).post();
            },

            success: CurrentUserActions.logout,
            error: CurrentUserActions.pageFault
        };
    },

    login() {
        return {
            remote(state, loginData) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiUserLogin).post(null, loginData).then((data) => {
                        resolve(data);

                    }).catch((error) => {
                        // If we get something other than 400, lets trigger a pageFault
                        if (error.statusCode !== 400) {
                            CurrentUserActions.pageFault(error);
                        }

                        // Trigger a login error
                        CurrentUserActions.failLogin(error);
                        reject({error, loginData});
                    });
                });
            },

            success: CurrentUserActions.didLogin,
            error: CurrentUserActions.failLogin
        };
    },

    register() {
        return {
            remote(state, registerData) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiSignup).post(null, registerData).then((data) => {
                        resolve(data);

                    }).catch((error) => {
                        // If we get something other than 400, lets trigger a pageFault
                        if (error.statusCode !== 400) {
                            CurrentUserActions.pageFault(error);
                        }

                        // Trigger a registering error
                        reject({error, registerData});
                    });
                });
            },

            success: CurrentUserActions.didRegister,
            error: CurrentUserActions.failRegister
        };
    },

    sendUserResetPasswordMail() {
        return {
            remote(state, userEmail) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiForgotPassword).post(null, userEmail).then((data) => {
                        resolve(data);

                    }).catch((error) => {
                        // If we get something other than 400, lets trigger a pageFault
                        if (error.statusCode !== 400) {
                            CurrentUserActions.pageFault(error);
                        }

                        // Trigger a registering error
                        reject({error, userEmail});
                    });
                });
            },

            success: CurrentUserActions.sentResetPasswordMail,
            error: CurrentUserActions.failResetPasswordEmail
        };
    },

    changeUserForgottenPassword() {
        return {
            remote(state, resetPasswordData) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiForgotPasswordToken).post(null, resetPasswordData).then((data) => {
                        resolve(data);

                    }).catch((error) => {
                        // If we get something other than 400, lets trigger a pageFault
                        if (error.statusCode !== 400) {
                            CurrentUserActions.pageFault(error);
                        }

                        // Trigger a registering error
                        if (typeof error.errors['uid_and_token_b64'] != typeof undefined && error.errors['uid_and_token_b64']) {
                            alert(error.errors['uid_and_token_b64']);
                        }

                        CurrentUserActions.failResetPassword(error);
                        reject({error});
                    });
                });
            },

            success: CurrentUserActions.passwordResetSuccess,
            error: CurrentUserActions.failResetPassword
        };
    },

    activateUser() {
        return {
            remote(state, token) {
                return new Promise((resolve, reject) => {
                    new SingleObjectResource(DJ_CONST.API.apiActivateUserToken).post(null, {token}).then((data) => {
                        resolve(data);
                    });
                });
            },

            success: CurrentUserActions.didLogin,
            error: CurrentUserActions.activateUserFail
        };
    }
};


class CurrentUserStore {
    constructor() {
        this.activeLanguage = null;
        this.isAuthenticated = false;
        this.profile = {};
        this.userDataReceived = false;
        this.loginError = {};
        this.registerError = {};
        this.retrievePasswordError = {};
        this.resetEmailError = {};
        this.saveError = {};
        this.settingsSaved = false;

        this.exportAsync(CurrentUserSource);
        this.bindActions(CurrentUserActions);
        this.exportPublicMethods({
            hasLoaded: this.hasLoaded,
            isPro: this.isPro,
            isBsn: this.isBsn
        });
    }

    onUpdateUser() {
        this.setState({settingsSaved: true, saveError: {}});
        setTimeout(x => {this.setState({settingsSaved: false})}, 5000);
    }

    onFailUpdateUser(error) {
        this.setState({saveError: error.updateError})
    }

    onMergeErrorsIntoState(errors) {
        this.setState({saveError: errors});
    }

    hasLoaded() {
        return !!this.getState().cacheTimestamp;
    }

    isPro() {
        const profile = this.getState().profile.profile;
        return profile ? profile.role === 'pro' : false;
    }

    isBsn() {
        const profile = this.getState().profile.profile;
        return profile ? profile.role === 'bsn' : false;
    }

    onFailResetPassword(retrievePasswordError) {
        this.setState(retrievePasswordError);
    }

    onFailResetPasswordEmail(resetEmailError) {
        this.setState(resetEmailError);
    }

    onClearUserDataReceived() {
        this.userDataReceived = false;
        this.cacheTimestamp = false;
    }

    onReceivedCurrentUser(user) {
        this.cacheTimestamp = new Date().getTime() / 1000;
        this.activeLanguage = user.language;
        this.userDataReceived = true;

        this.isAuthenticated = true;
        this.profile = user;
        this.loginError = {};
        this.registerError = {};

        this.profile.displayName = this.profile.first_name + ' ' + this.profile.last_name || this.profile.email;
    }

    onFailedCurrentUser(data) {
        this.cacheTimestamp = new Date().getTime() / 1000;
        this.activeLanguage = data.language;

        this.isAuthenticated = false;
        this.profile = {
            displayName: null
        };
    }

    onLogout() {
        // Force a reload so we don't have to worry
        //  about cleaning up sensitive data inside our stores
        window.location.reload();
    }

    onFailLogin(loginError) {
        this.setState(loginError);
    }

    onFailRegister(registerError) {
        this.setState(registerError);
    }

    onFailUpdateCompany(errors) {
        this.setState({saveError: errors.updateError});
    }

    onFailUpdatePassword(updatePasswordError) {
        this.setState(updatePasswordError);
    }

    onUpdateUserPassword(callback){
        if (typeof callback === 'function'){
            callback.call();
        }
    }

    onPageFault(pageFault) {
        this.setState(pageFault);

        if (process.env.NODE_ENV !== 'production') {
            console.error('pageFault', pageFault.message, pageFault.stack);
        }

        // Only do the logging if
        if (!(pageFault instanceof InvalidResponseCode) || [400, 403, 404].indexOf(pageFault.statusCode) === -1) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('pageFault: Snapshot', JSON.stringify(alt.takeSnapshot()));
            }

            if (process.env.NODE_ENV === 'production') {
                // TODO: You can log the errors here, for example one might use sentry for that
                // Note: To get current stores snapshot one can use: alt.takeSnapshot(). One can then
                //        restore the state locally by calling alt.bootstrap({snapshot...}). This enables
                //        us to easily reproduce errors.
            }
        }
    }
}

export default createStore(CurrentUserStore, 'CurrentUserStore', module);



/** WEBPACK FOOTER **
 ** ./app/src/stores/CurrentUserStore.js
 **/